import { createSlice } from "@reduxjs/toolkit";
import * as Immutable from "immutable";
import NewQueryActionHandler from "views/logic/NewQueryActionHandler";
import FindNewActiveQueryId from "views/logic/views/FindNewActiveQuery";
import View from "views/logic/views/View";
import { setGlobalOverrideQuery, execute, setSearchTypesToSearch } from "views/logic/slices/searchExecutionSlice";
import isEqualForSearch from "views/stores/isEqualForSearch";
import UpdateSearchForWidgets from "views/logic/views/UpdateSearchForWidgets";
import {
  selectActiveQuery,
  selectView,
  selectViewType,
  selectQueryById,
  selectSearchQueries,
  selectViewState,
  selectSearchQuery,
  selectSearch
} from "views/logic/slices/viewSelectors";
import createSearch from "views/logic/slices/createSearch";
import generateId from "logic/generateId";
import { createElasticsearchQueryString } from "views/logic/queries/Query";
import { pushIntoRevisions } from "views/logic/slices/undoRedoSlice";
const viewSlice = createSlice({
  name: "view",
  initialState: {
    view: void 0,
    isDirty: false,
    isNew: false,
    activeQuery: void 0
  },
  reducers: {
    setActiveQuery: (state, action) => ({
      ...state,
      activeQuery: action.payload
    }),
    setView: {
      reducer(state, action) {
        const [view, isDirty] = action.payload;
        return {
          ...state,
          view,
          isDirty: isDirty === void 0 ? state.isDirty : isDirty
        };
      },
      prepare(view, isDirty) {
        return {
          payload: [view, isDirty]
        };
      }
    },
    setIsNew: (state, action) => ({
      ...state,
      isNew: action.payload
    }),
    setIsDirty: (state, action) => ({
      ...state,
      isDirty: action.payload
    })
  }
});
export const viewSliceReducer = viewSlice.reducer;
export const { setView, setIsDirty, setIsNew, setActiveQuery } = viewSlice.actions;
export const isViewWidgetsEqualForSearch = (view, newView) => {
  var _a, _b;
  const oldWidgets = (_a = view == null ? void 0 : view.state) == null ? void 0 : _a.map((s) => s.widgets);
  const newWidgets = (_b = newView == null ? void 0 : newView.state) == null ? void 0 : _b.map((s) => s.widgets);
  return isEqualForSearch(oldWidgets, newWidgets);
};
const _recreateSearch = async (newView) => {
  const updatedView = UpdateSearchForWidgets(newView);
  const updatedSearch = await createSearch(updatedView.search);
  return updatedView.toBuilder().search(updatedSearch).build();
};
export const executeActiveQuery = () => (dispatch, getState) => {
  const view = selectView(getState());
  const search = selectSearch(getState());
  const activeQuery = selectActiveQuery(getState());
  return dispatch(execute({ search, activeQuery, widgetMapping: view.widgetMapping }));
};
export const selectQuery = (activeQuery) => async (dispatch, getState) => {
  const currentActiveQuery = selectActiveQuery(getState());
  dispatch(setActiveQuery(activeQuery));
  if (currentActiveQuery !== activeQuery) {
    dispatch(executeActiveQuery());
  }
};
export const loadView = (newView, recreateSearch = false) => async (dispatch, getState) => {
  const view = selectView(getState());
  if (recreateSearch || !isViewWidgetsEqualForSearch(view, newView)) {
    const updatedViewWithSearch = await _recreateSearch(newView);
    await dispatch(setView(updatedViewWithSearch));
    return dispatch(executeActiveQuery());
  }
  return dispatch(setView(newView));
};
const defaultUpdateViewOptions = { hasToPushRevision: true };
export const updateView = (newView, recreateSearch = false, options = defaultUpdateViewOptions) => async (dispatch, getState) => {
  const state = getState();
  const view = selectView(state);
  if (options.hasToPushRevision) {
    await dispatch(
      pushIntoRevisions({
        type: "view",
        state: {
          ...state.view
        }
      })
    );
  }
  if (recreateSearch || !isViewWidgetsEqualForSearch(view, newView)) {
    const updatedViewWithSearch = await _recreateSearch(newView);
    await dispatch(setView(updatedViewWithSearch, true));
    return dispatch(executeActiveQuery());
  }
  return dispatch(setView(newView, true));
};
export const updateQueries = (newQueries) => async (dispatch, getState) => {
  const view = selectView(getState());
  const { search } = view;
  const newSearch = search.toBuilder().newId().queries(newQueries).build();
  const searchAfterSave = await createSearch(newSearch);
  const newViewState = view.state.filter((_state, queryId) => !!newQueries.find((query) => query.id === queryId)).toMap();
  const newView = view.toBuilder().search(searchAfterSave).state(newViewState).build();
  return dispatch(updateView(newView));
};
export const addQuery = (query, viewState) => async (dispatch, getState) => {
  const state = getState();
  const view = selectView(state);
  const { search } = view;
  const newQueries = search.queries.add(query);
  const newViewStates = view.state.set(query.id, viewState);
  const newView = view.toBuilder().search(search.toBuilder().queries(newQueries).build()).state(newViewStates).build();
  return dispatch(updateView(newView, true)).then(() => dispatch(selectQuery(query.id))).then(() => query.id);
};
export const updateQuery = (queryId, query) => async (dispatch, getState) => {
  const state = getState();
  const view = selectView(state);
  const { queries } = view.search;
  const newQueries = queries.map((q) => q.id === queryId ? query : q).toOrderedSet();
  const newSearch = view.search.toBuilder().queries(newQueries).build();
  const newView = view.toBuilder().search(newSearch).build();
  return dispatch(updateView(newView, true));
};
export const removeQuery = (queryId) => async (dispatch, getState) => {
  const state = getState();
  const view = selectView(state);
  const activeQuery = selectActiveQuery(state);
  const { search } = view;
  const newQueries = search.queries.filter((query) => query.id !== queryId).toOrderedSet();
  const newViewState = view.state.remove(queryId);
  const newView = view.toBuilder().search(search.toBuilder().queries(newQueries).build()).state(newViewState).build();
  const indexedQueryIds = search.queries.map((query) => query.id).toList();
  const newActiveQuery = FindNewActiveQueryId(indexedQueryIds, activeQuery, Immutable.List([queryId]));
  await dispatch(selectQuery(newActiveQuery));
  await dispatch(updateView(newView, true));
};
export const createQuery = () => async (dispatch, getState) => {
  const viewType = selectViewType(getState());
  const [query, state] = await NewQueryActionHandler(viewType);
  return dispatch(addQuery(query, state));
};
export const duplicateQuery = (queryId) => async (dispatch, getState) => {
  const newId = generateId();
  const viewState = selectViewState(queryId)(getState());
  const newViewState = viewState.duplicate();
  const searchQuery = selectSearchQuery(queryId)(getState());
  const newSearchQuery = searchQuery.toBuilder().id(newId).build();
  await dispatch(addQuery(newSearchQuery, newViewState));
  return newId;
};
export const setQueriesOrder = (queryIds) => async (dispatch, getState) => {
  const queries = selectSearchQueries(getState());
  const newQueries = queryIds.map((id) => queries.find((q) => q.id === id)).toOrderedSet();
  return dispatch(updateQueries(newQueries));
};
export const mergeQueryTitles = (newQueryTitles) => async (dispatch, getState) => {
  const view = selectView(getState());
  let newState = view.state;
  newQueryTitles.forEach(({ titlesMap: newQueryTitle, queryId }) => {
    const newViewState = newState.get(queryId);
    let newViewStateTitles = newViewState.titles;
    newQueryTitle.forEach((titles, titleType) => {
      titles.forEach((titleValue, titleID) => {
        newViewStateTitles = newViewStateTitles.setIn([titleType, titleID], titleValue);
      });
    });
    newState = newState.set(queryId, newViewState.toBuilder().titles(newViewStateTitles).build());
  });
  const newView = view.toBuilder().state(newState).build();
  return dispatch(updateView(newView));
};
export const setQueryString = (queryId, newQueryString) => (dispatch, getState) => {
  const query = selectQueryById(queryId)(getState());
  const newQuery = query.toBuilder().query(createElasticsearchQueryString(newQueryString)).build();
  return dispatch(updateQuery(queryId, newQuery));
};
export const setTimerange = (queryId, timerange) => (dispatch, getState) => {
  const query = selectQueryById(queryId)(getState());
  const newQuery = query.toBuilder().timerange(timerange).build();
  return dispatch(updateQuery(queryId, newQuery));
};
export const updateQueryString = (queryId, newQueryString) => (dispatch, getState) => {
  const viewType = selectViewType(getState());
  if (viewType === View.Type.Search) {
    return dispatch(setQueryString(queryId, newQueryString));
  }
  return dispatch(setGlobalOverrideQuery(newQueryString)).then(() => dispatch(executeActiveQuery()));
};
export const updateViewState = (id, newViewState) => (dispatch, getState) => {
  const view = selectView(getState());
  const newState = view.state.set(id, newViewState);
  const newView = view.toBuilder().state(newState).build();
  return dispatch(updateView(newView));
};
export const setParameters = (newParameters) => async (dispatch, getState) => {
  const view = selectView(getState());
  const search = selectSearch(getState());
  const newSearch = search.toBuilder().parameters(newParameters).build();
  const newView = view.toBuilder().search(newSearch).build();
  return dispatch(updateView(newView, true));
};
export const setWidgetToSearch = (widgetId) => async (dispatch, getState) => {
  const { widgetMapping } = selectView(getState());
  const searchTypeIds = widgetMapping.get(widgetId);
  return dispatch(setSearchTypesToSearch(searchTypeIds == null ? void 0 : searchTypeIds.toArray()));
};
